'use client';

/**
 * BoatWizard — wizard 5-step in-modale per prenotazione barca.
 *
 * Chrome copiato da ExcursionWizard (WizardProgress + footer sticky Indietro/Avanti,
 * ultimo step = "Prenota", phase 'form'|'paying').
 *
 * FONTE UNICA DEL PREZZO — useBookingState:
 *   - Scarica dailyOverrides via boatDatePriceService (?with_markup=1) → stessa fonte
 *     di priceMap mostrato nel calendario DateRangePickerGrid.
 *   - Legge boat.seasonalPrices per il prezzo stagionale per-giorno.
 *   - Espone effectiveGrandTotal = prezzo date-aware coerente col calendario e
 *     con validateBookingPrices server-side.
 *   Il Riepilogo mostra effectiveBaseTotal + effectiveExtrasTotal + surcharge.
 *   handleBookNow invia lo stesso effectiveGrandTotal come totalPrice.
 *
 * Step attivi:
 *   1. Quando    — DateRangePickerGrid (useBookingCalendar)
 *   2. Ospiti    — stepper informativo (no prezzo per persona)
 *   3. Extra     — checkbox extra opzionali (skippato se assenti)
 *   4. Riepilogo — totali da useBookingState.effectiveGrandTotal
 *   5. Dati      — nome/email read-only, telefono editabile
 *   → "Prenota": createBooking → /api/payments/create → simulate|redirect
 */

import { useState, useMemo, useEffect, type ReactNode } from 'react';
import { useRouter } from 'next/navigation';
import { Check, Info, Calendar, Users } from 'lucide-react';
import { useAuth } from '@/app/providers';
import DateRangePickerGrid from '@/components/ui/date-range-picker/DateRangePickerGrid';
import { useBookingCalendar } from '@/components/booking-wizard/useBookingCalendar';
import StepPayment, { type PaymentStatus } from '@/components/booking/excursion/steps/StepPayment';
import { useBookingState } from '@/hooks/useBookingState';
import { createBooking } from '@/services/bookingService';
import type { BoatAsset } from '@/components/booking/UnifiedBookingModal';
import type { BlockedDateRange } from '@/components/ui/date-range-picker/types';
import { t } from '@/locales';

/* ── Step registry ────────────────────────────────────────────────────────── */

interface StepDef {
  key: string;
  label: string;
}

const BASE_STEPS: StepDef[] = [
  { key: 'when',     label: 'Quando' },
  { key: 'guests',   label: 'Ospiti' },
  { key: 'extras',   label: 'Extra' },
  { key: 'summary',  label: 'Riepilogo' },
  { key: 'customer', label: 'Dati' },
];

/* ── Wizard state (solo campi non gestiti da useBookingState) ─────────────── */

interface BoatWizardLocalState {
  phone: string;
  notes: string;
}

const INITIAL_LOCAL: BoatWizardLocalState = { phone: '', notes: '' };

/* ── Fascia giornaliera ───────────────────────────────────────────────────── */

/** Solo per singola giornata con price_half_day > 0. */
type Fascia = 'full' | 'half-morning' | 'half-afternoon';

/* ── Payment state ────────────────────────────────────────────────────────── */

interface PayState {
  status: PaymentStatus;
  errorMsg: string | null;
  errorCode: string | null;
  bookingId: string | null;
}

const INITIAL_PAY_STATE: PayState = {
  status: 'loading',
  errorMsg: null,
  errorCode: null,
  bookingId: null,
};

/* ── Helpers ──────────────────────────────────────────────────────────────── */

const euro = (n: number) =>
  Math.round(Number(n) || 0).toLocaleString('it-IT', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const fmtShort = (d: Date | null) =>
  d ? d.toLocaleDateString('it-IT', { day: '2-digit', month: 'short' }) : '—';

const toDateStr = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const partLabel = (p: 'full' | 'morning' | 'afternoon'): string =>
  p === 'full' ? 'Giornata' : p === 'morning' ? 'Mattina' : 'Pomeriggio';

const fmtDayStr = (ds: string): string =>
  new Date(ds + 'T12:00:00').toLocaleDateString('it-IT', { day: '2-digit', month: 'short' });

/* ── BorderChip — toggle bordo giorno (bordi parziali multi-giorno) ───────── */

function BorderChip({
  active, onClick, children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'px-3 py-1.5 rounded-full text-xs font-bold transition-all border whitespace-nowrap',
        active
          ? 'bg-sky-600 text-white border-sky-600 shadow-sm'
          : 'bg-white text-slate-500 border-slate-200 hover:border-sky-300',
      ].join(' ')}
    >
      {children}
    </button>
  );
}

/* ── WizardProgress (chrome identico a ExcursionWizard) ──────────────────── */

function WizardProgress({
  steps,
  currentIndex,
}: {
  steps: StepDef[];
  currentIndex: number;
}) {
  return (
    <div
      data-cid="boat-wizard-progress"
      className="flex items-start"
      role="list"
      aria-label="Step prenotazione barca"
    >
      {steps.map((step, i) => {
        const isCompleted = i < currentIndex;
        const isCurrent   = i === currentIndex;

        return (
          <div key={step.key} className="flex items-center flex-1 min-w-0" role="listitem">
            <div className="flex flex-col items-center gap-1 flex-1 min-w-0 px-0.5">
              <div
                aria-current={isCurrent ? 'step' : undefined}
                className={[
                  'w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 transition-colors',
                  isCompleted ? 'bg-sky-500 text-white'                                   : '',
                  isCurrent   ? 'bg-sky-600 text-white ring-2 ring-sky-300 ring-offset-1' : '',
                  !isCompleted && !isCurrent ? 'bg-slate-100 text-slate-400'              : '',
                ].join(' ')}
              >
                {isCompleted ? <Check size={13} strokeWidth={2.5} /> : i + 1}
              </div>
              <span
                className={[
                  'text-[10px] leading-tight text-center truncate w-full',
                  isCurrent   ? 'font-bold text-sky-700' : '',
                  isCompleted ? 'text-sky-500'           : '',
                  !isCompleted && !isCurrent ? 'text-slate-400' : '',
                ].join(' ')}
              >
                {step.label}
              </span>
            </div>

            {i < steps.length - 1 && (
              <div
                className={[
                  'h-px flex-1 shrink mx-1 mb-4 transition-colors',
                  i < currentIndex ? 'bg-sky-400' : 'bg-slate-200',
                ].join(' ')}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ── Props ────────────────────────────────────────────────────────────────── */

interface BoatWizardProps {
  boat: BoatAsset;
  blockedDates: BlockedDateRange[];
  initialStartDate?: string;
  initialEndDate?: string;
  /** Necessario per useBookingCalendar (avvia fetch slot/prezzi solo quando aperto). */
  isOpen?: boolean;
  /** F2 prezzo-per-porto: notifica il punto scelto → il parent rifetcha i prezzi di quel
   *  layer (boat.seasonalPrices) così il totale client combacia col server (drift-safe). */
  onDeparturePointChange?: (pointId: string) => void;
}

/* ── BoatWizard ───────────────────────────────────────────────────────────── */

export default function BoatWizard({
  boat,
  blockedDates,
  initialStartDate,
  initialEndDate,
  isOpen = true,
  onDeparturePointChange,
}: BoatWizardProps) {
  const router = useRouter();
  const { user } = useAuth();

  /* ── Calendario (date + priceMap per-giorno + disponibilità) ──────────── */
  const cal = useBookingCalendar({
    isOpen,
    boatId: String(boat.id),
    blockedDates,
    initialStartDate: initialStartDate ?? '',
    initialEndDate:   initialEndDate   ?? '',
  });

  // Date come oggetti: endDate = startDate quando solo 1 giorno selezionato.
  const startDate = cal.startDate ? new Date(cal.startDate + 'T12:00:00') : null;
  const endDate   = cal.endDate   ? new Date(cal.endDate   + 'T12:00:00') : startDate;

  /* ── useBookingState — FONTE UNICA DEL PREZZO ────────────────────────────
   * Questo hook:
   *   1. Scarica dailyOverrides via boatDatePriceService (?with_markup=1) —
   *      stessa sorgente di priceMap nel calendario DateRangePickerGrid.
   *   2. Legge boat.seasonalPrices per stagioni ricorrenti.
   *   3. Chiama calculatePriceBreakdown con tutti e due i layer → produce
   *      effectiveGrandTotal coerente col calendario E con validateBookingPrices
   *      server-side. Questo elimina il disallineamento calendario↔checkout.
   */
  const bs = useBookingState({
    boat,
    selectedStart:    startDate,
    selectedEnd:      endDate,
    initialGuests:    1,
    externalServices: [],
  });

  /* ── State locale (solo campi fuori da useBookingState) ─────────────────── */
  const [local, setLocal]               = useState<BoatWizardLocalState>(INITIAL_LOCAL);
  const [phase, setPhase]               = useState<'form' | 'paying'>('form');
  const [payState, setPayState]         = useState<PayState>(INITIAL_PAY_STATE);
  const [currentIndex, setCurrentIndex] = useState(0);

  /* ── Punto di partenza per-azienda ─────────────────────────────────────────
   * 0 punti → nessun campo. 1 punto → auto-selezionato (read-only).
   * >1 punti → select obbligatoria (blocca l'avanzamento finché non scelto). */
  const allDeparturePoints = boat.departure_points ?? [];
  const perPortPricing = (boat as { departure_same_price?: boolean }).departure_same_price === false;
  // Per-porto: mostra SOLO i punti prezzati (con layer proprio). I non-prezzati sono
  // esclusi (il server li bloccherebbe al submit). Non-per-porto → tutti (base condiviso).
  const departurePoints = allDeparturePoints.filter((p) => (p as { priced?: boolean }).priced !== false);
  // Nessun punto prezzato ma l'azienda è per-porto → barca non prenotabile finché i
  // prezzi porto non sono impostati (niente fallback base in modalità per-porto).
  const noPricedDeparturePoint = perPortPricing && allDeparturePoints.length > 0 && departurePoints.length === 0;
  const requiresDeparturePointChoice = departurePoints.length > 1;
  const [departurePointId, setDeparturePointId] = useState<string>(() => {
    if (departurePoints.length === 0) return '';
    if (departurePoints.length === 1) return departurePoints[0].id;
    return departurePoints.find((p) => p.is_default)?.id ?? departurePoints[0]?.id ?? '';
  });
  // F2: notifica il punto scelto (anche il default iniziale) → il parent rifetcha i prezzi del layer.
  useEffect(() => { onDeparturePointChange?.(departurePointId); }, [departurePointId]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Fascia giornaliera ─────────────────────────────────────────────────── */
  const [fascia, setFascia] = useState<Fascia>('full');

  // Disponibilità per-lato del giorno singolo (slotAvailability già fetchata dall'hook).
  // morning/afternoon === false ⇒ fascia occupata → chip disabilitato + auto-switch.
  const daySlots = cal.days === 1 && cal.startDate ? bs.slotAvailability?.[cal.startDate] : undefined;
  const morningFree   = !daySlots || daySlots.morning !== false;
  const afternoonFree = !daySlots || daySlots.afternoon !== false;

  /* ── Bordi parziali (multi-giorno): primo/ultimo giorno possono essere mezze.
   * Espressi via dayParts → path server dayPartPrice (parità con customBreakdown
   * lato client). Interni sempre 'full'. Solo se la barca ha price_half_day. */
  const [firstPart, setFirstPart] = useState<'full' | 'afternoon'>('full');
  const [lastPart, setLastPart]   = useState<'full' | 'morning'>('full');

  /**
   * Cambia fascia e aggiorna startTime/endTime in useBookingState.
   * calculatePriceBreakdown usa startTime/endTime per determinare
   * halfDays/fullDays → effectiveGrandTotal riflette la fascia.
   *
   * Intera  (09:00→18:00): diffDays=0, start<13, end>13 → fullDays=1 → €390
   * Mattina (09:00→13:00): diffDays=0, start<13, end=13 (non >13) → halfDays=1 → €225
   * Pomerig (14:00→18:00): diffDays=0, start>13, end>13 → halfDays=1 → €225
   */
  const handleFasciaChange = (newFascia: Fascia) => {
    setFascia(newFascia);
    if (newFascia === 'full') {
      bs.setStartTime('09:00');
      bs.setEndTime('18:00');
    } else if (newFascia === 'half-morning') {
      bs.setStartTime('09:00');
      bs.setEndTime(bs.cutoffStr);     // '13:00' — determina halfDays=1
    } else {
      bs.setStartTime(bs.afternoonStart); // '14:00' — determina halfDays=1
      bs.setEndTime('18:00');
    }
  };

  /* Ogni volta che la data cambia (anche da 1gg → range), torna alla fascia
   * "Giornata intera" così il prezzo default è sempre il full-day. */
  useEffect(() => {
    setFascia('full');
    bs.setStartTime('09:00');
    bs.setEndTime('18:00');
    setFirstPart('full');
    setLastPart('full');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cal.startDate, cal.endDate]);

  /* Se il lato scelto risulta occupato (slotAvailability), passa all'altro lato libero
   * → l'utente non resta su una mezza non prenotabile (il server la bloccherebbe 409). */
  useEffect(() => {
    if (fascia === 'half-morning' && !morningFree && afternoonFree) handleFasciaChange('half-afternoon');
    else if (fascia === 'half-afternoon' && !afternoonFree && morningFree) handleFasciaChange('half-morning');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [morningFree, afternoonFree, fascia]);

  /* Sync bordi → dayParts + perDayMode. Multi-giorno con half-day disponibile:
   * costruisce la mappa (interni 'full', bordi = scelta), attiva perDayMode solo
   * se almeno un bordo è mezza. Il server valida via dayPartPrice (stesso helper
   * del client) → nessun drift. Single-day usa il selettore fascia esistente. */
  useEffect(() => {
    if (cal.days <= 1 || !bs.halfDayAvailable) {
      if (bs.perDayMode) bs.setPerDayMode(false);
      return;
    }
    const keys = bs.daysList.map((d) => bs.dateKey(d));
    if (keys.length < 2) return;
    const map: Record<string, 'full' | 'morning' | 'afternoon'> = {};
    for (const k of keys) map[k] = 'full';
    if (firstPart === 'afternoon') map[keys[0]] = 'afternoon';
    if (lastPart === 'morning') map[keys[keys.length - 1]] = 'morning';
    bs.setDayParts(map);
    bs.setPerDayMode(firstPart !== 'full' || lastPart !== 'full');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cal.days, cal.startDate, cal.endDate, firstPart, lastPart, bs.halfDayAvailable]);

  /* ── Prefill telefono da profilo (una sola volta) ─────────────────────── */
  const profilePhone = [user?.phone_prefix, user?.phone_number].filter(Boolean).join(' ').trim();
  useEffect(() => {
    if (!profilePhone) return;
    setLocal((s) => {
      if (s.phone !== '') return s;
      return { ...s, phone: profilePhone };
    });
  }, [profilePhone]);

  /* ── Step list effettiva (extras rimosso se nessun opzionale) ──────────── */
  const activeSteps = useMemo(() => {
    const hasOptional = (boat.extras ?? []).some((e) => !e.mandatory);
    const base = hasOptional ? BASE_STEPS : BASE_STEPS.filter((s) => s.key !== 'extras');
    // Punto di partenza = PRIMO step quando >1 imbarco prezzato: il prezzo dipende dal
    // porto, quindi si sceglie prima di date/fasce.
    return requiresDeparturePointChoice ? [{ key: 'departure', label: 'Imbarco' }, ...base] : base;
  }, [boat.extras, requiresDeparturePointChoice]);

  const currentStep = activeSteps[currentIndex] ?? activeSteps[activeSteps.length - 1];
  const isFirst     = currentIndex === 0;
  const isLast      = currentIndex === activeSteps.length - 1;

  /* ── canNext ────────────────────────────────────────────────────────────── */
  const canNext = useMemo(() => {
    switch (currentStep.key) {
      case 'departure': return !!departurePointId; // porto scelto per proseguire
      case 'when':
        return !!cal.startDate && !noPricedDeparturePoint && (!requiresDeparturePointChoice || !!departurePointId);
      case 'customer': return true; // contatti read-only dal profilo; solo le note sono opzionali
      default:         return true;
    }
  }, [currentStep.key, cal.startDate, local.phone, requiresDeparturePointChoice, departurePointId, noPricedDeparturePoint]);

  /* ── Derived per i render ───────────────────────────────────────────────── */
  const optionalExtras  = useMemo(() => (boat.extras ?? []).filter((e) => !e.mandatory), [boat.extras]);
  const mandatoryExtras = useMemo(() => (boat.extras ?? []).filter((e) =>  e.mandatory), [boat.extras]);

  /* Totale comprensivo di servizi esterni (= 0 nel wizard barca standalone) */
  const totalForDisplay = bs.effectiveGrandTotal + bs.externalServicesTotal;

  /* ── Navigazione ────────────────────────────────────────────────────────── */
  const handleBack = () => { if (!isFirst) setCurrentIndex((i) => i - 1); };

  /* ── Submit + pagamento ─────────────────────────────────────────────────── */

  const handleBookNow = async () => {
    if (!user) {
      const ret = window.location.pathname + window.location.search;
      router.push(`/login?returnUrl=${encodeURIComponent(ret)}`);
      return;
    }
    if (!startDate || !endDate) return;
    if (requiresDeparturePointChoice && !departurePointId) return;

    setPhase('paying');
    setPayState({ status: 'loading', errorMsg: null, errorCode: null, bookingId: null });

    try {
      /* ── Totale date-aware da useBookingState (stessa fonte del Riepilogo) */
      const nights     = bs.effectiveFullDays + bs.effectiveHalfDays;
      const totalPrice = Number(totalForDisplay.toFixed(2));

      /* ── 1. Crea prenotazione ──────────────────────────────────────────── */
      const result = await createBooking({
        boatId:                    String(boat.id),
        guestId:                   user.id,
        checkIn:                   toDateStr(startDate),
        checkOut:                  toDateStr(endDate),
        checkInTime:               bs.startTime,
        checkOutTime:              bs.endTime,
        guestsCount:               bs.guests,
        nights,
        basePrice:                 bs.effectiveBaseTotal,
        extrasTotal:               bs.effectiveExtrasTotal,
        shortStaySurchargeAmount:  bs.priceBreakdown.shortStaySurchargeAmount  || undefined,
        shortStaySurchargePercent: bs.priceBreakdown.shortStaySurchargePercent || undefined,
        totalPrice,
        selectedExtras:            bs.priceBreakdown.extrasBreakdown,
        paymentMethod:             'CREDIT_CARD',
        dayParts:                  bs.perDayMode ? bs.dayParts : undefined,
        departurePointId:          departurePointId || undefined,
      });

      if (!result?.data?.id) throw new Error('Prenotazione non creata. Riprova.');
      const bookingId = result.data.id;

      /* Fase E: prenotazione tardiva (oltre cutoff) → niente pagamento, attende l'operatore. */
      if ((result as { lateApproval?: boolean }).lateApproval) {
        setPayState({ status: 'late-pending', errorMsg: null, errorCode: null, bookingId });
        return;
      }

      /* ── 2. Avvia pagamento ─────────────────────────────────────────────── */
      const payRes = await fetch('/api/payments/create', {
        method:      'POST',
        headers:     { 'Content-Type': 'application/json' },
        credentials: 'include',
        body:        JSON.stringify({ booking_id: bookingId }),
      });
      const pj = (await payRes.json().catch(() => ({}))) as {
        simulated?: boolean;
        paymentUrl?: string;
        error?: string;
      };

      /* Staging MOLLIE_FORCE_SIMULATE=true → pannello simulate in-modale */
      if (payRes.ok && pj.simulated) {
        setPayState({ status: 'simulate', errorMsg: null, errorCode: null, bookingId });
        return;
      }

      /* Pagamento reale Mollie → redirect */
      if (payRes.ok && pj.paymentUrl) {
        setPayState({ status: 'redirecting', errorMsg: null, errorCode: null, bookingId });
        window.location.href = pj.paymentUrl;
        return;
      }

      throw new Error(pj.error ?? 'Pagamento non avviato.');
    } catch (err) {
      if ((err as { status?: number }).status === 401) {
        const ret = window.location.pathname + window.location.search;
        router.push(`/login?returnUrl=${encodeURIComponent(ret)}`);
        return;
      }
      setPayState({
        status:    'error',
        errorMsg:  (err as Error).message || 'Errore imprevisto. Riprova.',
        errorCode: (err as { code?: string }).code ?? null,
        bookingId: null,
      });
    }
  };

  /* ── Pagamento simulato: POST /api/payments/simulate ─────────────────── */

  const handleSimulatePay = async (outcome: 'paid' | 'failed') => {
    const bookingId = payState.bookingId;
    if (!bookingId) return;
    setPayState((s) => ({ ...s, status: 'loading' }));
    try {
      const res = await fetch('/api/payments/simulate', {
        method:      'POST',
        headers:     { 'Content-Type': 'application/json' },
        credentials: 'include',
        body:        JSON.stringify({ booking_id: bookingId, status: outcome }),
      });
      const j = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string };
      if (res.ok && j.ok !== false) {
        router.push(`/booking/confirmed?booking_id=${bookingId}`);
        return;
      }
      throw new Error(j.error ?? `HTTP ${res.status}`);
    } catch (err) {
      setPayState((s) => ({
        ...s,
        status:    'error',
        errorMsg:  (err as Error).message || 'Errore nel pagamento simulato.',
        errorCode: null,
      }));
    }
  };

  const handleNext = () => {
    if (isLast) { void handleBookNow(); return; }
    setCurrentIndex((i) => i + 1);
  };

  /* ── Gate login: chiedi l'accesso SUBITO, non all'ultimo click ────────────
   * Prima l'anonimo attraversava tutto il wizard e veniva rediretto a /login
   * solo su "Prenota" (perdendo la compilazione). */
  if (!user) {
    const goLogin = () => {
      const ret = window.location.pathname + window.location.search;
      router.push(`/login?returnUrl=${encodeURIComponent(ret)}`);
    };
    return (
      <div data-cid="bw-login-required" className="flex flex-col items-center justify-center py-14 gap-4 text-center">
        <div className="w-14 h-14 rounded-full bg-sky-50 flex items-center justify-center">
          <Users size={26} className="text-sky-500" />
        </div>
        <div>
          <p className="text-base font-bold text-slate-800">Accedi per prenotare</p>
          <p className="text-sm text-slate-500 mt-1">Per completare la prenotazione di <b>{boat.name}</b> serve un account.</p>
        </div>
        <button type="button" data-cid="bw-login-cta" onClick={goLogin} className="px-6 py-2.5 rounded-xl bg-sky-600 text-white text-sm font-bold hover:bg-sky-700">
          Accedi o registrati
        </button>
        <p className="text-[11px] text-slate-400">Tornerai qui dopo l&apos;accesso.</p>
      </div>
    );
  }

  /* ── Phase: paying ─────────────────────────────────────────────────────── */

  if (phase === 'paying') {
    return (
      <div data-cid="boat-wizard" className="flex flex-col gap-2">
        <StepPayment
          status={payState.status}
          errorMsg={payState.errorMsg}
          errorCode={payState.errorCode}
          bookingId={payState.bookingId}
          onSimulatePay={handleSimulatePay}
          onBack={() => setPhase('form')}
        />
      </div>
    );
  }

  /* ── Phase: form ────────────────────────────────────────────────────────── */

  return (
    <div data-cid="boat-wizard" className="flex flex-col gap-2">
      {/* Progress orizzontale */}
      <WizardProgress steps={activeSteps} currentIndex={currentIndex} />

      <hr className="border-slate-100" />

      {/* Step corrente — pb per non finire sotto il footer sticky */}
      <div className="flex-1 pb-20">

            {/* ── Step 0: Punto di partenza (multi-porto, il prezzo dipende dal porto) ── */}
            {currentStep.key === 'departure' && (
              <div data-cid="boat-wizard-departure" className="flex flex-col gap-3">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{t('booking.departurePoint')}</p>
                  <p className="text-sm text-slate-500">Il prezzo può cambiare in base al porto d&apos;imbarco. Scegli da dove parti.</p>
                </div>
                <div className="flex flex-col gap-2">
                  {departurePoints.map((p) => {
                    const sel = departurePointId === p.id;
                    const fp = (p as { from_price?: number | null }).from_price;
                    const fu = (p as { from_unit?: string | null }).from_unit;
                    const ul = fu === 'week' ? '/sett' : fu === 'month' ? '/mese' : '/giorno';
                    return (
                      <button
                        key={p.id}
                        type="button"
                        data-cid={`boat-departure-opt-${p.id}`}
                        onClick={() => setDeparturePointId(p.id)}
                        className={['flex items-center gap-3 w-full text-left px-4 py-3 rounded-xl border transition-colors',
                          sel ? 'border-sky-500 bg-sky-50' : 'border-slate-200 hover:border-sky-300'].join(' ')}
                      >
                        <span className="text-sky-600 text-lg shrink-0">⚓</span>
                        <span className="flex-1 min-w-0 flex items-center gap-2 font-semibold text-slate-800 text-sm">
                          <span className="truncate">{p.name}</span>
                          {p.is_default && <span className="text-[9px] font-bold uppercase tracking-wide text-sky-600 border border-sky-200 rounded px-1 shrink-0">più vicino</span>}
                        </span>
                        {fp != null && (
                          <span className="text-sm font-black text-slate-900 tabular-nums whitespace-nowrap shrink-0">da €{euro(Number(fp))}<span className="text-[11px] text-slate-400 font-medium">{ul}</span></span>
                        )}
                        <span className={['w-5 h-5 rounded-full border grid place-items-center text-xs flex-none',
                          sel ? 'bg-sky-600 border-sky-600 text-white' : 'border-slate-300 text-transparent'].join(' ')}>✓</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

        {/* ── Step 1: Quando ────────────────────────────────────────────── */}
        {currentStep.key === 'when' && (
          <div data-cid="boat-wizard-step-when" className="flex flex-col gap-4">
            <div className="rounded-2xl border border-slate-100 overflow-hidden">
              <DateRangePickerGrid {...cal.gridProps} />
            </div>
            {cal.startDate ? (
              <p className="text-sm text-slate-500 text-center">
                <span className="inline-flex items-center gap-1.5">
                  <Calendar size={13} className="text-sky-500" />
                  {fmtShort(startDate)} → {fmtShort(endDate)}
                </span>
                {' · '}
                <b>{cal.days} giorn{cal.days === 1 ? 'o' : 'i'}</b>
              </p>
            ) : (
              <p className="text-sm text-slate-400 text-center">
                Seleziona le date per continuare
              </p>
            )}

            {/* ── Punto di partenza per-azienda ─────────────────────────── */}
            {departurePoints.length === 1 && (
              <div data-cid="booking-departure-point" className="flex flex-col gap-1">
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  {t('booking.departurePoint')}
                </p>
                <p className="text-sm text-slate-600">
                  {t('booking.departureFrom')}: <b>{departurePoints[0].name}</b>
                </p>
              </div>
            )}

            {noPricedDeparturePoint && (
              <div data-cid="booking-departure-nopricing" className="rounded-xl border border-amber-200 bg-amber-50 p-3">
                <p className="text-sm font-bold text-amber-800">Barca non prenotabile online</p>
                <p className="text-xs text-amber-700 mt-0.5">Nessun punto di partenza ha un prezzo impostato. Contatta l&apos;operatore.</p>
              </div>
            )}

            {/* Multi-porto (>1 imbarco prezzato) → scelto nel PRIMO step "Imbarco". */}

            {/* ── Fascia giornaliera (solo 1 giorno + price_half_day > 0) ── */}
            {cal.days === 1 && bs.halfDayAvailable && (
              <div data-cid="boat-wizard-fascia" className="flex flex-col gap-2.5">
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  Fascia giornaliera
                </p>

                {/* Chip principale: Intera / Mezza */}
                <div className="flex gap-2 flex-wrap">
                  <button
                    type="button"
                    data-cid="boat-fascia-intera"
                    onClick={() => handleFasciaChange('full')}
                    className={[
                      'px-4 py-2 rounded-full text-sm font-bold transition-all border',
                      fascia === 'full'
                        ? 'bg-sky-600 text-white border-sky-600 shadow-sm'
                        : 'bg-white text-slate-600 border-slate-200 hover:border-sky-300',
                    ].join(' ')}
                  >
                    Giornata intera
                    {bs.fasciaPrices ? <span className="ml-1.5 font-black opacity-90">€{euro(bs.fasciaPrices.full)}</span> : null}
                  </button>

                  <button
                    type="button"
                    data-cid="boat-fascia-mezza"
                    onClick={() => {
                      if (fascia === 'full') handleFasciaChange(morningFree ? 'half-morning' : 'half-afternoon');
                    }}
                    className={[
                      'px-4 py-2 rounded-full text-sm font-bold transition-all border',
                      fascia !== 'full'
                        ? 'bg-sky-600 text-white border-sky-600 shadow-sm'
                        : 'bg-white text-slate-600 border-slate-200 hover:border-sky-300',
                    ].join(' ')}
                  >
                    Mezza giornata
                    {bs.fasciaPrices ? <span className="ml-1.5 font-black opacity-90">da €{euro(Math.min(bs.fasciaPrices.morning, bs.fasciaPrices.afternoon))}</span> : null}
                  </button>
                </div>

                {/* Sub-chip Mattina / Pomeriggio (visibili solo se mezza scelta) */}
                {fascia !== 'full' && (
                  <div className="flex gap-2 pl-1">
                    <button
                      type="button"
                      data-cid="boat-fascia-mattina"
                      disabled={!morningFree}
                      onClick={() => morningFree && handleFasciaChange('half-morning')}
                      className={[
                        'px-3 py-1.5 rounded-full text-xs font-bold transition-all border',
                        !morningFree
                          ? 'bg-slate-50 text-slate-300 border-slate-200 cursor-not-allowed line-through'
                          : fascia === 'half-morning'
                            ? 'bg-slate-700 text-white border-slate-700'
                            : 'bg-white text-slate-500 border-slate-200 hover:border-slate-400',
                      ].join(' ')}
                    >
                      Mattina
                      {!morningFree
                        ? <span className="ml-1 text-[10px] font-bold">occupata</span>
                        : bs.fasciaPrices ? <span className="ml-1 font-black opacity-90">€{euro(bs.fasciaPrices.morning)}</span> : null}
                    </button>

                    <button
                      type="button"
                      data-cid="boat-fascia-pomeriggio"
                      disabled={!afternoonFree}
                      onClick={() => afternoonFree && handleFasciaChange('half-afternoon')}
                      className={[
                        'px-3 py-1.5 rounded-full text-xs font-bold transition-all border',
                        !afternoonFree
                          ? 'bg-slate-50 text-slate-300 border-slate-200 cursor-not-allowed line-through'
                          : fascia === 'half-afternoon'
                            ? 'bg-slate-700 text-white border-slate-700'
                            : 'bg-white text-slate-500 border-slate-200 hover:border-slate-400',
                      ].join(' ')}
                    >
                      Pomeriggio
                      {!afternoonFree
                        ? <span className="ml-1 text-[10px] font-bold">occupato</span>
                        : bs.fasciaPrices ? <span className="ml-1 font-black opacity-90">€{euro(bs.fasciaPrices.afternoon)}</span> : null}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* ── Bordi parziali (multi-giorno + price_half_day > 0) ───────── */}
            {cal.days > 1 && bs.halfDayAvailable && (
              <div data-cid="boat-wizard-borders" className="flex flex-col gap-3">
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  Inizio e fine noleggio
                </p>

                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm text-slate-600 truncate">
                    Primo giorno · {fmtShort(startDate)}
                  </span>
                  <div className="flex gap-1.5 shrink-0">
                    <BorderChip active={firstPart === 'full'} onClick={() => setFirstPart('full')}>
                      Giornata
                    </BorderChip>
                    <BorderChip active={firstPart === 'afternoon'} onClick={() => setFirstPart('afternoon')}>
                      Solo pomeriggio
                    </BorderChip>
                  </div>
                </div>

                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm text-slate-600 truncate">
                    Ultimo giorno · {fmtShort(endDate)}
                  </span>
                  <div className="flex gap-1.5 shrink-0">
                    <BorderChip active={lastPart === 'full'} onClick={() => setLastPart('full')}>
                      Giornata
                    </BorderChip>
                    <BorderChip active={lastPart === 'morning'} onClick={() => setLastPart('morning')}>
                      Solo mattina
                    </BorderChip>
                  </div>
                </div>

                <p className="text-[11px] text-slate-400">
                  I giorni centrali sono sempre a giornata intera.
                </p>
              </div>
            )}
          </div>
        )}

        {/* ── Step 2: Ospiti ────────────────────────────────────────────── */}
        {currentStep.key === 'guests' && (
          <div data-cid="boat-wizard-step-guests" className="flex flex-col gap-5 py-2">
            <p className="text-base font-bold text-slate-800">Quanti ospiti?</p>

            <div className="flex items-center justify-between p-4 rounded-xl border border-slate-200">
              <div className="flex flex-col gap-0.5">
                <span className="text-sm font-bold text-slate-800 flex items-center gap-1.5">
                  <Users size={14} className="text-sky-500" />
                  Ospiti
                </span>
                {boat.capacity > 0 && (
                  <span className="text-xs text-slate-400">Max {boat.capacity}</span>
                )}
              </div>

              <div className="flex items-center gap-4">
                <button
                  type="button"
                  data-cid="boat-wizard-guests-minus"
                  onClick={() => bs.setGuests(Math.max(1, bs.guests - 1))}
                  className="w-9 h-9 rounded-full border border-slate-300 font-bold text-slate-600 hover:bg-slate-50 transition-colors"
                  aria-label="Riduci ospiti"
                >
                  −
                </button>
                <span
                  data-cid="boat-wizard-guests-value"
                  className="w-8 text-center text-lg font-bold text-slate-900"
                >
                  {bs.guests}
                </span>
                <button
                  type="button"
                  data-cid="boat-wizard-guests-plus"
                  onClick={() =>
                    bs.setGuests(
                      boat.capacity ? Math.min(boat.capacity, bs.guests + 1) : bs.guests + 1,
                    )
                  }
                  className="w-9 h-9 rounded-full border border-slate-300 font-bold text-slate-600 hover:bg-slate-50 transition-colors"
                  aria-label="Aumenta ospiti"
                >
                  +
                </button>
              </div>
            </div>

            <p className="text-xs text-slate-400">
              Il numero di ospiti è indicativo. Il prezzo non varia per persona.
            </p>
          </div>
        )}

        {/* ── Step 3: Extra ─────────────────────────────────────────────── */}
        {currentStep.key === 'extras' && (
          <div data-cid="boat-wizard-step-extras" className="flex flex-col gap-4 py-2">
            <p className="text-base font-bold text-slate-800">Extra</p>

            {optionalExtras.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-6">
                Nessun extra opzionale disponibile.
              </p>
            ) : (
              <div className="flex flex-col gap-3">
                {optionalExtras.map((e) => {
                  const isChecked = bs.selectedExtras.includes(e.id);
                  return (
                    <label
                      key={e.id}
                      data-cid={`boat-wizard-extra-${e.id}`}
                      className={[
                        'flex items-start gap-3 px-4 py-3 rounded-xl border transition-all cursor-pointer',
                        isChecked
                          ? 'border-sky-300 bg-sky-50'
                          : 'border-slate-200 bg-white hover:border-sky-200',
                      ].join(' ')}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => bs.toggleExtra(e.id)}
                        className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 accent-sky-600"
                        aria-label={e.label}
                      />
                      <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                        <span className="text-sm font-semibold text-slate-800">{e.label}</span>
                        {e.description && (
                          <p className="text-xs text-slate-400 leading-snug">{e.description}</p>
                        )}
                        <span className="text-sm font-bold text-sky-700 mt-0.5">
                          €{euro(Number(e.price))}
                          {e.priceType === 'per_day'    && ' /giorno'}
                          {e.priceType === 'per_person' && ' /persona'}
                        </span>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}

            {/* Extra obbligatori — informativi */}
            {mandatoryExtras.length > 0 && (
              <div className="pt-3 border-t border-slate-100">
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2">
                  Sempre inclusi
                </p>
                {mandatoryExtras.map((e) => (
                  <div
                    key={e.id}
                    className="flex justify-between items-center text-sm text-slate-500 py-1"
                  >
                    <span>
                      {e.label}
                      <span className="ml-1.5 text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-sky-100 text-sky-700">
                        Incluso
                      </span>
                    </span>
                    <span className="font-semibold">€{euro(Number(e.price))}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Step 4: Riepilogo ─────────────────────────────────────────── */}
        {currentStep.key === 'summary' && (
          <div data-cid="boat-wizard-step-summary" className="flex flex-col gap-5 py-2">
            {/* Header date + ospiti */}
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-500">
              <span className="inline-flex items-center gap-1.5">
                <Calendar size={13} className="text-sky-500 shrink-0" />
                {fmtShort(startDate)} → {fmtShort(endDate)}
                {' · '}
                <b className="text-slate-700">
                  {cal.days} giorn{cal.days === 1 ? 'o' : 'i'}
                </b>
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Users size={13} className="text-sky-500 shrink-0" />
                {bs.guests} {bs.guests === 1 ? 'ospite' : 'ospiti'}
              </span>
            </div>

            <hr className="border-slate-100" />

            {bs.hasDates ? (
              <div className="flex flex-col gap-3">
                {/* Base noleggio: per-giorno (bordi parziali) oppure aggregata */}
                {bs.perDayMode && bs.customBreakdown ? (
                  <div data-cid="boat-wizard-summary-perday" className="flex flex-col gap-1.5">
                    {bs.customBreakdown.items.map((it) => (
                      <div
                        key={it.date}
                        data-cid={`boat-wizard-summary-day-${it.date}`}
                        className="flex items-center justify-between gap-4"
                      >
                        <span className="text-sm text-slate-600">
                          {fmtDayStr(it.date)} · <span className="text-slate-400">{partLabel(it.part)}</span>
                        </span>
                        <span className="text-sm font-semibold text-slate-800 tabular-nums whitespace-nowrap">
                          €{euro(it.price)}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div
                    data-cid="boat-wizard-summary-base"
                    className="flex items-start justify-between gap-4"
                  >
                    <div className="flex flex-col min-w-0">
                      <span className="text-sm font-semibold text-slate-800">Noleggio</span>
                      <span className="text-xs text-slate-400">
                        {bs.effectiveFullDays > 0 &&
                          `${bs.effectiveFullDays} giorn${bs.effectiveFullDays === 1 ? 'o' : 'i'}`}
                        {bs.effectiveFullDays > 0 && bs.effectiveHalfDays > 0 && ' + '}
                        {bs.effectiveHalfDays > 0 && `${bs.effectiveHalfDays} mezza giornata`}
                        {bs.hasSeasonal && ' · prezzi stagionali'}
                      </span>
                    </div>
                    <span className="text-sm font-bold text-slate-900 tabular-nums whitespace-nowrap">
                      €{euro(bs.effectiveBaseTotal)}
                    </span>
                  </div>
                )}

                {/* Extra selezionati */}
                {bs.priceBreakdown.extrasBreakdown.map((eb) => {
                  const extra = (boat.extras ?? []).find((e) => e.id === eb.extraId);
                  if (!extra) return null;
                  return (
                    <div
                      key={eb.extraId}
                      data-cid={`boat-wizard-summary-extra-${eb.extraId}`}
                      className="flex items-start justify-between gap-4"
                    >
                      <span className="text-sm text-slate-700">{extra.label}</span>
                      <span className="text-sm font-semibold text-slate-800 tabular-nums whitespace-nowrap">
                        €{euro(eb.totalPrice)}
                      </span>
                    </div>
                  );
                })}

                {/* Short-stay surcharge */}
                {bs.priceBreakdown.shortStaySurchargeAmount > 0 && (
                  <div
                    data-cid="boat-wizard-summary-surcharge"
                    className="flex items-start justify-between gap-4"
                  >
                    <span className="text-sm text-slate-500">
                      Suppl. soggiorno breve ({bs.priceBreakdown.shortStaySurchargePercent}%)
                    </span>
                    <span className="text-sm font-semibold text-slate-800 tabular-nums whitespace-nowrap">
                      €{euro(bs.priceBreakdown.shortStaySurchargeAmount)}
                    </span>
                  </div>
                )}

                {/* Totale */}
                <div
                  data-cid="boat-wizard-summary-total"
                  className="flex items-baseline justify-between gap-4 border-t border-slate-200 pt-4"
                >
                  <span className="text-sm font-bold text-slate-800">Totale</span>
                  <span className="text-2xl font-black text-slate-900 tabular-nums">
                    €{euro(totalForDisplay)}
                  </span>
                </div>

                {/* Paghi ora — #11: dipende dalla modalità incasso del vendor/barca.
                    commission_only → online solo la commissione, saldo a bordo (qualitativo:
                    l'importo esatto lo calcola il server, niente split duplicato client-side). */}
                {(boat as { paymentCollectionMode?: string }).paymentCollectionMode === 'commission_only' ? (
                  <div
                    data-cid="boat-wizard-summary-payment"
                    className="rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 flex flex-col gap-1"
                  >
                    <div className="flex items-center justify-between gap-4">
                      <span className="text-sm font-semibold text-slate-700">Paghi online ora</span>
                      <span className="text-sm font-black text-sky-700">Commissione di prenotazione</span>
                    </div>
                    <p className="text-xs text-slate-500">Il saldo del noleggio lo versi direttamente all&apos;operatore al check-in.</p>
                  </div>
                ) : (
                  <div
                    data-cid="boat-wizard-summary-payment"
                    className="rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 flex items-center justify-between gap-4"
                  >
                    <span className="text-sm font-semibold text-slate-700">Paghi online ora</span>
                    <span className="text-lg font-black text-sky-700 tabular-nums">
                      €{euro(totalForDisplay)}
                    </span>
                  </div>
                )}

                {/* Cauzione — riga solo informativa */}
                {!!boat.securityDeposit && boat.securityDeposit > 0 && (
                  <div
                    data-cid="boat-wizard-summary-security-deposit"
                    className="flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3"
                    role="note"
                    aria-label="Informazione cauzione"
                  >
                    <Info size={14} className="text-amber-600 mt-0.5 shrink-0" aria-hidden="true" />
                    <p className="text-xs text-amber-800 leading-relaxed">
                      <strong>Cauzione: €{euro(boat.securityDeposit)}</strong> — preautorizzazione
                      all&apos;imbarco. L&apos;importo non viene addebitato ora.
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-slate-400 text-center py-4">
                Torna allo step Quando per selezionare le date.
              </p>
            )}
          </div>
        )}

        {/* ── Step 5: Dati ──────────────────────────────────────────────── */}
        {currentStep.key === 'customer' && (
          <div data-cid="boat-wizard-step-customer" className="flex flex-col gap-5 py-2">
            {/* Mini-recap */}
            {cal.startDate && (
              <div className="rounded-xl bg-slate-50 border border-slate-100 px-3 py-2.5 text-xs text-slate-500 font-medium leading-snug">
                {boat.name} · {fmtShort(startDate)} → {fmtShort(endDate)} ·{' '}
                {bs.guests} {bs.guests === 1 ? 'ospite' : 'ospiti'}
              </div>
            )}

            <p className="text-base font-bold text-slate-800">Dati del cliente</p>

            {/* Nome — read-only */}
            <div className="flex flex-col gap-1">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                Nome
              </span>
              <div className="px-3 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-sm font-semibold text-slate-500 select-none">
                {user?.name || [user?.first_name, user?.last_name].filter(Boolean).join(' ') || '—'}
              </div>
            </div>

            {/* Email — read-only */}
            <div className="flex flex-col gap-1">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                Email
              </span>
              <div className="px-3 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-sm font-semibold text-slate-500 select-none truncate">
                {user?.email || '—'}
              </div>
            </div>

            {/* Telefono — read-only dal profilo */}
            <div data-cid="boat-wizard-customer-phone" className="flex flex-col gap-1">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                Telefono
              </span>
              <div className="px-3 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-sm font-semibold text-slate-500 select-none">
                {profilePhone || '—'}
              </div>
              <p className="text-[11px] text-slate-400">
                {profilePhone
                  ? 'Verrà verificato in fase di check-in / KYC.'
                  : 'Aggiungi il numero nel tuo profilo per velocizzare il check-in.'}
              </p>
            </div>

            {/* Note — opzionale */}
            <div data-cid="boat-wizard-customer-notes" className="flex flex-col gap-1">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                Note (opzionale)
              </span>
              <textarea
                rows={3}
                placeholder="Richieste particolari, domande all'operatore…"
                value={local.notes}
                onChange={(e) => setLocal((s) => ({ ...s, notes: e.target.value }))}
                className="px-3 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-800 placeholder:text-slate-300 focus:outline-none focus:ring-2 focus:ring-sky-400/40 focus:border-sky-400 transition-colors resize-none"
              />
            </div>
          </div>
        )}

      </div>

      {/* ── Footer nav sticky ────────────────────────────────────────────── */}
      <div
        data-cid="boat-wizard-nav"
        className="sticky bottom-0 bg-white border-t border-slate-200 flex items-center justify-between gap-3 py-3 -mx-4 px-4 md:-mx-6 md:px-6 -mb-4 md:-mb-6"
      >
        <button
          type="button"
          data-cid="boat-wizard-back"
          disabled={isFirst}
          onClick={handleBack}
          className="px-5 py-2.5 text-sm font-semibold rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
        >
          Indietro
        </button>

        <button
          type="button"
          data-cid="boat-wizard-next"
          disabled={!canNext}
          onClick={handleNext}
          className="px-6 py-2.5 text-sm font-bold rounded-full bg-sky-600 text-white hover:bg-sky-700 shadow-md disabled:opacity-50 disabled:shadow-none transition-all"
        >
          {isLast ? 'Prenota' : 'Avanti'}
        </button>
      </div>
    </div>
  );
}
